import { useQueries } from 'react-query';

import { KimThreadFileResponse } from './useGetKimThreadFile';
import { APIBaseChronos } from '@/api/hosts';
import { ApiError, QueryEntity } from '@/api/types';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface UseGetKimThreadFileChunksBatchArgs {
  matterId?: string;
  subsetId?: string;
  threadId?: string;
  chunkIds: string[];
}

/**
 * Batch sibling of `useGetKimThreadFile` for resolving many thread-file chunks
 * at once (each response carries the parent file plus the chunk's bounding box).
 * Query keys mirror the single-chunk hook so the cache is shared.
 */
export function useGetKimThreadFileChunksBatch({
  matterId,
  subsetId,
  threadId,
  chunkIds,
}: UseGetKimThreadFileChunksBatchArgs) {
  const { fetchConfigGET } = useGetFetchConfig();

  const results = useQueries(
    chunkIds.map((chunkId) => ({
      queryKey: [QueryEntity.KimThreadFile, { chunkId, fileId: null }],
      queryFn: async (): Promise<KimThreadFileResponse> => {
        const res = await fetch(
          `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${threadId}/file-chunks/${chunkId}`,
          fetchConfigGET,
        );
        if (!res.ok) {
          const error = await res.json();
          throw new ApiError(res.status, error.message);
        }
        return res.json();
      },
      enabled: !!matterId && !!subsetId && !!threadId && !!chunkId,
      refetchOnWindowFocus: false,
    })),
  );

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);
  const data = results.map((r) => r.data).filter((c): c is KimThreadFileResponse => !!c);

  return { isLoading, isError, data };
}
